// src/automation/components/ActivityLogPage.js
//
// Activity Log (admin only), pinned at the bottom of the sidebar. Three tabs:
//   • Activity — every action for a chosen ISO week (one file per week in S3):
//                visits, config / mapping / access / global edits (with what
//                changed), adds, deletes, restores, runs, denied attempts.
//   • Runs     — the run history (same data as "Recent runs" on the cover),
//                filterable by period / task / status / type.
//   • Visits   — who opened the console that week, per login.
// Every tab exports exactly what's on screen as CSV.

import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, ScrollText, Undo2 } from "lucide-react";
import { getActivityWeeks, getActivityWeek, listRuns, revertChange } from "../api/automation_data";

const ACTION_LABELS = {
  visit: "Visit",
  save_config: "Config edited",
  save_map: "Mapping edited",
  save_access: "Access changed",
  save_global: "Global config edited",
  add_automation: "Automation added",
  update_registry: "Sidebar entry updated",
  remove_registry: "Sidebar entry removed",
  delete_automation: "Automation deleted",
  restore_automation: "Automation restored",
  clear_runs: "Run history cleared",
  run: "Run",
  denied: "Denied",
  revert: "Reverted",
};

// A change can be undone if it saved a copy of the previous version (config,
// mapping, access, global, report design, an earlier revert) or was a delete.
const canRevert = (e) =>
  e.ok && !e.reverted_at && (!!e.snapshot_key || e.action === "delete_automation");
const label = (a) => ACTION_LABELS[a] || a;

function fmtTs(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? ts
    : d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// ── CSV ──────────────────────────────────────────────────────────────────────
function toCsv(headers, rows) {
  const esc = (v) => {
    const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\r\n");
}
function downloadCsv(filename, headers, rows) {
  // BOM so Excel opens UTF-8 correctly.
  const blob = new Blob(["\uFEFF" + toCsv(headers, rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
function changesText(changes) {
  if (!changes) return "";
  if (Array.isArray(changes)) {
    return changes.map((c) => `${c.path}: ${JSON.stringify(c.before)} → ${JSON.stringify(c.after)}`).join(" | ");
  }
  const c = changes;
  return `rows ${c.rows_before}→${c.rows_after}, +${c.added} / -${c.removed}` +
    (c.headers_changed ? ", headers changed" : "");
}

// ── change details (expanded row) ─────────────────────────────────────────────
function ChangeDetails({ changes }) {
  if (!changes) return null;
  if (Array.isArray(changes)) {
    return (
      <table className="auto-table auto-al-changes">
        <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
        <tbody>
          {changes.map((c, i) => (
            <tr key={i}>
              <td className="auto-table-mono">{c.path}</td>
              <td className="auto-al-before">{c.before == null ? "—" : JSON.stringify(c.before)}</td>
              <td className="auto-al-after">{c.after == null ? "(removed)" : JSON.stringify(c.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className="auto-al-mapdiff">
      <div>
        Rows {changes.rows_before} → {changes.rows_after} · <b>+{changes.added}</b> added · <b>-{changes.removed}</b> removed
        {changes.headers_changed ? " · headers changed" : ""}
      </div>
      {changes.added_sample?.length > 0 && (
        <div><span className="auto-al-after">Added:</span>
          <ul>{changes.added_sample.map((r, i) => <li key={i} className="auto-table-mono">{r}</li>)}</ul></div>
      )}
      {changes.removed_sample?.length > 0 && (
        <div><span className="auto-al-before">Removed:</span>
          <ul>{changes.removed_sample.map((r, i) => <li key={i} className="auto-table-mono">{r}</li>)}</ul></div>
      )}
    </div>
  );
}

function Ok({ ok }) {
  return ok ? (
    <span className="auto-status auto-status--ok"><CheckCircle2 size={13} /> OK</span>
  ) : (
    <span className="auto-status auto-status--err"><XCircle size={13} /> Failed</span>
  );
}

// ── Activity tab ──────────────────────────────────────────────────────────────
function ActivityTab({ week, events, loading, onChanged }) {
  const qc = useQueryClient();
  const [login, setLogin] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState({});
  const [reverting, setReverting] = useState(null);
  const [notice, setNotice] = useState(null);

  const doRevert = async (e) => {
    const what = `${label(e.action)} on "${e.target}" by ${e.login} (${fmtTs(e.ts)})`;
    if (!window.confirm(`Revert this change?\n\n${what}\n\nThe previous version is put back, and the revert itself is logged (and can be undone).`)) return;
    setReverting(e.id);
    setNotice(null);
    try {
      let res = await revertChange(e.week || week, e.id, false);
      if (!res.success && res.conflict) {
        if (!window.confirm(`${res.summary}\n\nRevert anyway?`)) {
          setNotice({ type: "err", msg: "Revert cancelled — nothing changed." });
          return;
        }
        res = await revertChange(e.week || week, e.id, true);
      }
      setNotice({ type: res.success ? "ok" : "err", msg: res.summary || (res.success ? "Reverted" : "Revert failed") });
      if (res.success) {
        // Anything the change touched may now look different.
        for (const k of ["automationGroups", "automationBootstrap", "taskConfig", "map", "reportLayout",
          "autoPermissions", "deletedAutomations"]) qc.invalidateQueries({ queryKey: [k] });
        onChanged && onChanged();
      }
    } catch (err) {
      setNotice({ type: "err", msg: err?.message || "Revert failed" });
    } finally {
      setReverting(null);
    }
  };

  const logins = useMemo(() => [...new Set(events.map((e) => e.login))].sort(), [events]);
  const actions = useMemo(() => [...new Set(events.map((e) => e.action))].sort(), [events]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...events].reverse().filter((e) =>
      (!login || e.login === login) &&
      (!action || e.action === action) &&
      (!q || `${e.target} ${e.summary} ${changesText(e.changes)}`.toLowerCase().includes(q))
    );
  }, [events, login, action, search]);

  const exportCsv = () =>
    downloadCsv(`activity_${week}.csv`,
      ["timestamp_utc", "login", "action", "target", "ok", "run_type", "summary", "changes", "reverted_by", "reverted_at_utc"],
      rows.map((e) => [e.ts, e.login, e.action, e.target, e.ok, e.run_type || "", e.summary, changesText(e.changes),
        e.reverted_by || "", e.reverted_at || ""]));

  return (
    <>
      <div className="auto-al-filters">
        <select className="auto-eb-input" value={login} onChange={(e) => setLogin(e.target.value)}>
          <option value="">All people</option>
          {logins.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="auto-eb-input" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{label(a)}</option>)}
        </select>
        <input className="auto-eb-input" placeholder="Search task / summary / change…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={exportCsv} disabled={!rows.length}>
          <Download size={12} /> Export CSV ({rows.length})
        </button>
      </div>
      {notice && (
        <div className={`auto-eb-alert ${notice.type === "err" ? "auto-eb-alert--err" : "auto-eb-alert--ok"}`}>
          <span>{notice.msg}</span>
        </div>
      )}
      {loading ? (
        <div className="auto-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="auto-empty">No activity for this filter.</div>
      ) : (
        <div className="auto-table-wrap auto-table-wrap--scroll auto-al-wrap">
          <table className="auto-table">
            <thead><tr><th></th><th>When (local)</th><th>Who</th><th>Action</th><th>Target</th><th>Result</th><th>Summary</th><th></th></tr></thead>
            <tbody>
              {rows.map((e, i) => {
                const key = `${e.ts}-${i}`;
                const has = !!e.changes;
                return (
                  <React.Fragment key={key}>
                    <tr className={has ? "auto-al-row--click" : ""} onClick={() => has && setOpen((o) => ({ ...o, [key]: !o[key] }))}>
                      <td>{has ? (open[key] ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : null}</td>
                      <td>{fmtTs(e.ts)}</td>
                      <td className="auto-table-mono">{e.login}</td>
                      <td>{label(e.action)}{e.run_type ? <span className="auto-al-sub"> · {e.run_type}</span> : null}</td>
                      <td className="auto-table-mono">{e.target}</td>
                      <td>
                        <Ok ok={e.ok} />
                        {e.reverted_at && (
                          <div className="auto-al-sub" title={`Reverted ${fmtTs(e.reverted_at)}`}>
                            reverted by {e.reverted_by}
                          </div>
                        )}
                      </td>
                      <td className="auto-al-summary" title={e.summary}>{e.summary}</td>
                      <td>
                        {canRevert(e) && (
                          <button
                            className="admin-btn admin-btn--sm admin-btn--outline"
                            title="Put back the version from before this change"
                            disabled={reverting === e.id}
                            onClick={(ev) => { ev.stopPropagation(); doRevert(e); }}
                          >
                            <Undo2 size={12} /> {reverting === e.id ? "Reverting…" : "Revert"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {has && open[key] && (
                      <tr className="auto-al-detail"><td colSpan={8}><ChangeDetails changes={e.changes} /></td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Runs tab ──────────────────────────────────────────────────────────────────
const PERIODS = [["1", "Last 24h"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["all", "All stored"]];

function RunsTab() {
  const { data: runs = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["automationRuns"],
    queryFn: () => listRuns({ limit: 2000 }),
    staleTime: 2 * 60 * 1000,
  });
  const [period, setPeriod] = useState("1");
  const [task, setTask] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");

  const tasks = useMemo(() => [...new Set(runs.map((r) => r.task))].sort(), [runs]);
  const rows = useMemo(() => {
    const since = period === "all" ? 0 : Date.now() - Number(period) * 24 * 3600 * 1000;
    return runs.filter((r) => {
      const t = new Date(r.timestamp).getTime();
      const type = r.type || "";
      const k = /scheduled/.test(type) ? "scheduled" : /test/.test(type) ? "test" : "manual";
      return (!since || t >= since) && (!task || r.task === task) &&
        (!status || (status === "ok" ? r.success : !r.success)) && (!kind || k === kind);
    });
  }, [runs, period, task, status, kind]);

  const exportCsv = () =>
    downloadCsv(`runs_${period === "all" ? "all" : `last_${period}d`}.csv`,
      ["timestamp_utc", "task", "type", "run_by", "success", "summary"],
      rows.map((r) => [r.timestamp, r.task, r.type, r.actor || (/scheduled/.test(r.type || "") ? "scheduler" : ""), r.success, r.summary]));

  return (
    <>
      <div className="auto-al-filters">
        <select className="auto-eb-input" value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="auto-eb-input" value={task} onChange={(e) => setTask(e.target.value)}>
          <option value="">All tasks</option>
          {tasks.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="auto-eb-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All run types</option>
          <option value="scheduled">Scheduled</option>
          <option value="manual">Manual</option>
          <option value="test">Test</option>
        </select>
        <select className="auto-eb-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any result</option>
          <option value="ok">Success</option>
          <option value="fail">Failed</option>
        </select>
        <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={12} /> Refresh
        </button>
        <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={exportCsv} disabled={!rows.length}>
          <Download size={12} /> Export CSV ({rows.length})
        </button>
      </div>
      <p className="auto-rd-hint">The run history keeps the most recent 2,000 runs.</p>
      {isLoading ? (
        <div className="auto-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="auto-empty">No runs for this filter.</div>
      ) : (
        <div className="auto-table-wrap auto-table-wrap--scroll auto-al-wrap">
          <table className="auto-table">
            <thead><tr><th>When (local)</th><th>Task</th><th>Run by</th><th>Type</th><th>Result</th><th>Summary</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{fmtTs(r.timestamp)}</td>
                  <td className="auto-table-mono">{r.task}</td>
                  <td className="auto-table-mono">{r.actor || (/scheduled/.test(r.type || "") ? "scheduler" : "—")}</td>
                  <td>{r.type}</td>
                  <td><Ok ok={r.success} /></td>
                  <td className="auto-al-summary" title={r.summary}>{r.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Visits tab ────────────────────────────────────────────────────────────────
function VisitsTab({ week, events, loading }) {
  const people = useMemo(() => {
    const by = {};
    for (const e of events) {
      const p = (by[e.login] = by[e.login] || { login: e.login, visits: 0, actions: 0, runs: 0, last: "" });
      if (e.action === "visit") p.visits += 1;
      else if (e.action === "run") p.runs += 1;
      else p.actions += 1;
      if (!p.last || e.ts > p.last) p.last = e.ts;
    }
    return Object.values(by).filter((p) => p.login !== "scheduler").sort((a, b) => (a.last < b.last ? 1 : -1));
  }, [events]);

  const exportCsv = () =>
    downloadCsv(`visits_${week}.csv`, ["login", "visits", "changes", "runs", "last_seen_utc"],
      people.map((p) => [p.login, p.visits, p.actions, p.runs, p.last]));

  return (
    <>
      <div className="auto-al-filters">
        <span className="auto-rd-hint">
          {people.length} people this week · a visit is counted at most once per 30 minutes per person
        </span>
        <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={exportCsv} disabled={!people.length}>
          <Download size={12} /> Export CSV
        </button>
      </div>
      {loading ? (
        <div className="auto-empty">Loading…</div>
      ) : people.length === 0 ? (
        <div className="auto-empty">No visits recorded this week.</div>
      ) : (
        <div className="auto-table-wrap auto-al-wrap">
          <table className="auto-table">
            <thead><tr><th>Who</th><th>Visits</th><th>Changes</th><th>Runs</th><th>Last seen (local)</th></tr></thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.login}>
                  <td className="auto-table-mono">{p.login}</td>
                  <td>{p.visits}</td><td>{p.actions}</td><td>{p.runs}</td><td>{fmtTs(p.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function ActivityLogPage() {
  const [tab, setTab] = useState("activity");
  const [week, setWeek] = useState("");

  const { data: weekInfo, error: weeksError } = useQuery({
    queryKey: ["activityWeeks"],
    queryFn: getActivityWeeks,
    staleTime: 60 * 1000,
  });
  const weeks = useMemo(() => {
    const list = weekInfo?.weeks || [];
    const cur = weekInfo?.current;
    return cur && !list.includes(cur) ? [cur, ...list] : list;
  }, [weekInfo]);
  const activeWeek = week || weekInfo?.current || weeks[0] || "";

  const { data: events = [], isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["activityWeek", activeWeek],
    queryFn: () => getActivityWeek(activeWeek),
    enabled: !!activeWeek,
    staleTime: 30 * 1000,
  });

  return (
    <div className="auto-task">
      <div className="auto-task-header">
        <div className="auto-main-title-row">
          <ScrollText size={20} />
          <h2 className="auto-task-title">Activity Log</h2>
        </div>
        <p className="auto-task-subtitle">
          Who visited, what they changed, and every run — stored as one file per week. Times are shown in your local time; exports are UTC.
        </p>
      </div>

      {(weeksError || error) && (
        <div className="auto-eb-alert auto-eb-alert--err"><span>{(weeksError || error).message}</span></div>
      )}

      <div className="auto-subtabs">
        {[["activity", "Activity"], ["runs", "Runs"], ["visits", "Visits"]].map(([id, l]) => (
          <button key={id} className={`auto-subtab ${tab === id ? "auto-subtab--active" : ""}`} onClick={() => setTab(id)}>
            {l}
          </button>
        ))}
        {tab !== "runs" && (
          <span className="auto-al-week">
            <select className="auto-eb-input" value={activeWeek} onChange={(e) => setWeek(e.target.value)}>
              {weeks.map((w) => (
                <option key={w} value={w}>{w}{w === weekInfo?.current ? " (this week)" : ""}</option>
              ))}
            </select>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={12} className={isFetching ? "auto-eb-spin" : ""} />
            </button>
          </span>
        )}
      </div>

      {tab === "activity" && (
        <ActivityTab week={activeWeek} events={events} loading={isLoading} onChanged={() => refetch()} />
      )}
      {tab === "runs" && <RunsTab />}
      {tab === "visits" && <VisitsTab week={activeWeek} events={events} loading={isLoading} />}
    </div>
  );
}
